import { useState } from "react";
import { Link } from "react-router-dom";
import { useSite } from "@/components/site/SiteLayout";
import { useJobs } from "@/hooks/useSiteData";
import useSeo from "@/hooks/useSeo";
import { api } from "@/lib/api";

function ApplyForm({ position, onDone, requireCv = false }) {
  const [note, setNote] = useState({ cls: "", text: "" });
  const [sending, setSending] = useState(false);
  const [cvName, setCvName] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const f = e.target;
    const cvFile = f.cv?.files?.[0];
    if (requireCv && !cvFile) {
      setNote({ cls: "err", text: "Carica il tuo CV (PDF o Word) per candidarti a questa posizione." });
      return;
    }
    setSending(true);
    setNote({ cls: "", text: "Invio in corso..." });
    try {
      let cv_path = "";
      let cv_name = "";
      if (cvFile) {
        const fd = new FormData();
        fd.append("file", cvFile);
        const up = await api.post("/upload-cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
        cv_path = up.data.path;
        cv_name = up.data.filename;
      }
      const { data } = await api.post("/contact", {
        tipo: "Candidatura — Lavora con noi",
        nome: f.nome.value,
        email: f.email.value,
        telefono: f.telefono.value,
        residenza: f.residenza.value,
        posizione: position || "Candidatura spontanea",
        cv_path,
        cv_name,
        messaggio: f.messaggio.value,
      });
      setNote({ cls: "ok", text: data.message });
      f.reset();
      setCvName("");
      if (onDone) setTimeout(onDone, 1600);
    } catch (err) {
      const d = err.response?.data?.detail;
      setNote({ cls: "err", text: typeof d === "string" ? d : "Errore nell'invio. Riprova più tardi." });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="apply-form">
      <div className="field"><label>Nome e Cognome *</label><input name="nome" required placeholder="Mario Rossi" data-testid="apply-nome" /></div>
      <div className="field"><label>Luogo in cui vivi *</label><input name="residenza" required placeholder="Es. Napoli (NA)" data-testid="apply-residenza" /></div>
      <div className="field"><label>Numero di telefono *</label><input name="telefono" required placeholder="+39 333 1234567" data-testid="apply-telefono" /></div>
      <div className="field"><label>Indirizzo email *</label><input name="email" type="email" required placeholder="mario@esempio.it" data-testid="apply-email" /></div>
      <div className="field">
        <label>Curriculum Vitae {requireCv ? "*" : "(facoltativo)"}</label>
        <input name="cv" type="file" accept=".pdf,.doc,.docx" required={requireCv} onChange={(e) => setCvName(e.target.files?.[0]?.name || "")} data-testid="apply-cv" />
        <span style={{ fontSize: ".8rem", color: "var(--ink-soft)" }}>{cvName ? `File: ${cvName}` : "Formati accettati: PDF, DOC, DOCX (max 10MB)"}</span>
      </div>
      <div className="field"><label>Presentazione (facoltativa)</label><textarea name="messaggio" rows="3" placeholder="Parlaci di te e della tua esperienza..." data-testid="apply-messaggio" /></div>
      <button className="btn full" disabled={sending} data-testid="apply-submit">Invia candidatura</button>
      {note.text && <p className={`form-note ${note.cls}`} data-testid="apply-note">{note.text}</p>}
    </form>
  );
}

export default function Lavora() {
  const c = useSite();
  useSeo("Lavora con noi", "Posizioni aperte in POWERCOM: candidati direttamente a un'offerta di lavoro o invia una candidatura spontanea.");
  const { data: jobs = [], isLoading } = useJobs();
  const openJobs = jobs.filter((j) => j.is_open);
  const [activeJob, setActiveJob] = useState(null);

  return (
    <>
      <section className="page-hero">
        <div className="container reveal">
          <span className="eyebrow light">Lavora con noi</span>
          <h1>Costruiamo il futuro, insieme.</h1>
          <p>Cerchiamo persone motivate che vogliano crescere in un ambiente dinamico e professionale.</p>
        </div>
      </section>

      {/* Posizioni aperte */}
      <section className="section">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow">Posizioni aperte</span>
            <h2>Le opportunità di lavoro attive.</h2>
            <p>{c.work_text}</p>
          </div>

          {isLoading ? (
            <p className="empty">Caricamento…</p>
          ) : openJobs.length === 0 ? (
            <div className="empty" data-testid="jobs-empty">
              <h2>Nessuna posizione aperta al momento.</h2>
              <p style={{ marginTop: 12 }}>Puoi comunque inviarci una candidatura spontanea con il form qui sotto.</p>
            </div>
          ) : (
            <div className="jobs-list" data-testid="jobs-list">
              {openJobs.map((j) => (
                <article className="job-card reveal" key={j.id} data-testid={`job-card-${j.id}`}>
                  <div className="job-main">
                    <h3>{j.title}</h3>
                    <div className="job-tags">
                      {j.location && <span className="job-tag">📍 {j.location}</span>}
                      {j.type && <span className="job-tag">🕒 {j.type}</span>}
                    </div>
                  </div>
                  <div className="job-actions">
                    <Link className="btn outline" to={`/lavora-con-noi/${j.id}`} data-testid={`job-details-${j.id}`}>Dettagli</Link>
                    <button className="btn" onClick={() => setActiveJob(j)} data-testid={`job-apply-${j.id}`}>Candidati</button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Candidatura spontanea */}
      <section className="section section-soft" id="form-candidatura">
        <div className="container contact-grid">
          <div className="contact-info reveal">
            <span className="eyebrow">Candidatura spontanea</span>
            <h2>Non trovi la posizione giusta?</h2>
            <p>Inviaci comunque i tuoi dati: valuteremo il tuo profilo per opportunità presenti e future.</p>
            <ul className="ticks">{(c.work_points || []).map((p, i) => <li key={i}>{p}</li>)}</ul>
          </div>
          <div className="form-card reveal">
            <ApplyForm position="Candidatura spontanea" />
          </div>
        </div>
      </section>

      {/* Modale candidatura diretta */}
      {activeJob && (
        <div className="modal-overlay" onClick={() => setActiveJob(null)} data-testid="apply-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setActiveJob(null)} aria-label="Chiudi" data-testid="apply-modal-close">×</button>
            <span className="eyebrow">Candidatura</span>
            <h3 className="modal-title">{activeJob.title}</h3>
            <div className="job-tags" style={{ marginBottom: 18 }}>
              {activeJob.location && <span className="job-tag">📍 {activeJob.location}</span>}
              {activeJob.type && <span className="job-tag">🕒 {activeJob.type}</span>}
            </div>
            <ApplyForm position={activeJob.title} onDone={() => setActiveJob(null)} requireCv={true} />
          </div>
        </div>
      )}
    </>
  );
}
